import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import useAudio from './UseAudio';
import WaitingRoom from './waiting-room/WaitingRoom';
import Board28 from './card-games/trick-games/Board28';
import DonkeyBoard from './card-games/donkey/BoardDonkey'; 
import BoardPoker from './card-games/poker/BoardPoker';
import LudoBoard from './boards/ludo/LudoBoard';
import MatchStartSplash from './common/MatchStartSplash';

export default function GameController({ gameState: gs, roomId, myPlayerId, send, error, trickNotif, tunnelUrl, clearError, onLeave, isSpectator }) {
  const { playSound } = useAudio();

  // 1. CLEAN VARIABLE ACCESS (Backend now guarantees this is a string)
  const currentPhase = gs?.phase || gs?.status || 'waiting';
  const currentMode = gs?.mode;

  // 2. INSTANT RELOAD INITIALIZATION
  const prevPhase = useRef(currentPhase);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isBoardVisible, setIsBoardVisible] = useState(currentPhase !== 'waiting');
  const [roundBanner, setRoundBanner] = useState(null);

  const sprintRounds = gs?.sprint_rounds || 0;
  const currentRound = (gs?.total_rounds_played || 0) + 1;
  const isSuddenDeath = gs?.in_sudden_death;
  const isFinalRound = isSuddenDeath || (sprintRounds > 0 && currentRound === sprintRounds);

  // 3. EFFECTS
  useEffect(() => {
    // Clean return to waiting room
    if (currentPhase === 'waiting') {
      setIsBoardVisible(false);
      setIsTransitioning(false);
      setRoundBanner(null);
      prevPhase.current = currentPhase;
      return;
    }

    const wasWaiting = prevPhase.current === 'waiting';
    const wasRoundOver = prevPhase.current === 'round_over';
    const isStartingGame = currentPhase === 'playing' || currentPhase === 'bidding';
    
    let timer;
    let bannerTimer;
    
    // Trigger Splash Screen if explicitly leaving the waiting room
    if (isStartingGame && wasWaiting && !isBoardVisible) {
      setIsTransitioning(true);
      playSound('startGame');

      timer = setTimeout(() => {
        setIsTransitioning(false);
        setIsBoardVisible(true);
        playSound('cardShuffle');
      }, 2500);
    } else if (isStartingGame && wasRoundOver) {
      // Start of a new round
      if (isFinalRound) {
        setIsTransitioning(true);
        playSound('startGame');

        timer = setTimeout(() => {
          setIsTransitioning(false);
          playSound('cardShuffle');
        }, 2500);
      } else {
        playSound('cardShuffle');
      }
    } else if (!isBoardVisible && currentPhase !== 'waiting') {
      // Failsafe / Mid-game reload bypass
      setIsBoardVisible(true);
    }

    prevPhase.current = currentPhase;

    return () => {
      if (timer) clearTimeout(timer);
      if (bannerTimer) clearTimeout(bannerTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhase, currentMode]);

  // Robust Round Banner Trigger
  const lastRoundSeen = useRef(-1);

  useEffect(() => {
    if (roundBanner) {
      const t = setTimeout(() => setRoundBanner(null), 3000);
      return () => clearTimeout(t);
    }
  }, [roundBanner]);

  useEffect(() => {
    if (isBoardVisible && currentPhase !== 'waiting' && currentPhase !== 'round_over') {
      const current = gs?.total_rounds_played || 0;
      if (current > lastRoundSeen.current) {
        lastRoundSeen.current = current;
        setRoundBanner(isSuddenDeath ? "SUDDEN DEATH!" : `ROUND ${current + 1}`);
      }
    } else if (currentPhase === 'waiting') {
      lastRoundSeen.current = -1;
    }
  }, [isBoardVisible, gs?.total_rounds_played, currentPhase, isSuddenDeath]);

  if (!gs) return null;

  return (
    <>
      <AnimatePresence>
        {isTransitioning && (
          <MatchStartSplash 
            mode={currentMode} 
            logo="/assets/resources/The-Cheetu-Club.svg"
            gameState={gs}
          />
        )}      
      </AnimatePresence>

      {currentPhase === 'waiting' ? (
        <WaitingRoom 
          gameState={gs} 
          roomId={roomId}
          myPlayerId={myPlayerId} 
          send={send} 
          error={error}
          clearError={clearError}
          tunnelUrl={tunnelUrl}
          onLeave={onLeave} 
          isSpectator={isSpectator}
        />
      ) : (
        <div className="game-board-wrapper">
          <AnimatePresence>
            {roundBanner && (
              <motion.div
                initial={{ opacity: 0, y: 30, x: '-50%', scale: 0.9 }}
                animate={{ opacity: 1, y: 0, x: '-50%', scale: 1 }}
                exit={{ opacity: 0, y: -20, x: '-50%', scale: 0.9 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                style={{
                  position: 'absolute', bottom: '32%', left: '50%',
                  background: 'linear-gradient(145deg, rgba(20, 30, 20, 0.95) 0%, rgba(5, 10, 5, 0.95) 100%)', 
                  padding: '16px 50px', 
                  borderRadius: '100px',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  borderTop: '1px solid rgba(212, 175, 55, 0.5)',
                  color: '#facc15', 
                  fontSize: '1.6rem', 
                  fontWeight: '800',
                  boxShadow: '0 15px 40px rgba(0,0,0,0.8), 0 0 25px rgba(212, 175, 55, 0.15), inset 0 1px 0 rgba(255,255,255,0.05)', 
                  zIndex: 5000,
                  textTransform: 'uppercase', 
                  letterSpacing: '6px', 
                  backdropFilter: 'blur(12px)',
                  textAlign: 'center',
                  textShadow: '0 2px 6px rgba(0,0,0,0.9)'
                }}
              >
                {roundBanner}
              </motion.div>
            )}
          </AnimatePresence>
          {isBoardVisible && (currentMode === '28' || currentMode === '56') && (
            <Board28 
              gameState={gs} 
              myPlayerId={myPlayerId} 
              send={send} 
              error={error} 
              trickNotif={trickNotif} 
              clearError={clearError}
              onLeave={onLeave} 
              isSpectator={isSpectator}
            />
          )}

          {isBoardVisible && currentMode === 'donkey' && (
            <DonkeyBoard 
              state={gs} 
              send={send} 
              myPlayerId={myPlayerId} 
              onLeave={onLeave} 
              isSpectator={isSpectator}
            />
          )}

          {isBoardVisible && currentMode === 'poker' && (
            <BoardPoker 
              state={gs} 
              send={send} 
              myPlayerId={myPlayerId} 
              onLeave={onLeave} 
              isSpectator={isSpectator}
            />
          )}

          {isBoardVisible && currentMode === 'ludo' && (
            <LudoBoard 
              gameState={gs} 
              myPlayerId={myPlayerId} 
              send={send} 
              onLeave={onLeave} 
              isSpectator={isSpectator}
            />
          )}
          
          {!isBoardVisible && !isTransitioning && (
            <div style={{color: 'white', textAlign: 'center', padding: '50px', zIndex: 9999, position: 'relative'}}>
              Manual Override: Transition stuck. <br/>
              <button onClick={() => setIsBoardVisible(true)} style={{padding: '10px', marginTop: '10px', cursor: 'pointer', background: '#333', color: 'white', border: '1px solid #666', borderRadius: '4px'}}>
                Force Show Board
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}